import type { Lang } from "@/i18n/translations";
import carBmw7 from "@/assets/car-bmw-serie-7.jpg";
import carClasseV from "@/assets/car-classe-v.jpg";
import carDs9 from "@/assets/car-ds-9.jpg";
import carBmwI7 from "@/assets/car-bmw-i7.jpg";
import carSprinter from "@/assets/svc-sprinter-hero.jpg";
import carCoach from "@/assets/svc-coach-hero.jpg";

type CarL10n = { cat: string; name: string; pax: string; capacityNote?: string[]; feat: string; description: string; equipment: string[]; usage: string[] };

export type Car = {
  slug: string;
  n: string;
  image: string;
  i18n: Record<Lang, CarL10n>;
};

const CAR_IMAGES: Record<string, string> = {
  "bmw-serie-7": carBmw7,
  "mercedes-classe-v": carClasseV,
  "ds-9": carDs9,
  "bmw-i7": carBmwI7,
  "sprinter-vip": carSprinter,
  "autocar": carCoach,
};

const RAW_CARS: Omit<Car, "image">[] = [
  {
    slug: "bmw-serie-7",
    n: "N°01",
    i18n: {
      fr: {
        cat: "Limousine berline première classe",
        name: "BMW Série 7",
        pax: "1 — 3 passagers · 3 bagages",
        feat: "Cuir Merino · Wi-Fi 5G · Climatisation 4 zones",
        description:
          "Notre limousine de référence à Paris. Une cabine pensée comme un salon roulant : insonorisation poussée, suspension pneumatique.",
        equipment: ["Cuir Merino pleine fleur", "Climatisation 4 zones indépendantes", "Wi-Fi 5G illimité", "Sièges arrière massants et inclinables", "Vitres acoustiques teintées", "Tablettes de travail repliables", "Eaux Évian / Perrier offertes", "Presse du jour à la demande"],
        usage: ["Transferts aéroport premium", "Rendez-vous d'affaires sensibles", "Mise à disposition journée"],
      },
      en: {
        cat: "First-class limousine sedan",
        name: "BMW 7 Series",
        pax: "1 — 3 passengers · 3 bags",
        feat: "Merino leather · 5G Wi-Fi · 4-zone climate",
        description:
          "Our flagship limousine sedan in Paris. A cabin conceived as a rolling lounge: heavy soundproofing, air suspension, adjustable ambient lighting.",
        equipment: ["Full-grain Merino leather", "4 independent climate zones", "Unlimited 5G Wi-Fi", "Reclining massage rear seats", "Tinted acoustic windows", "Folding work tablets", "Complimentary Évian / Perrier water", "Daily press on request"],
        usage: ["Premium airport transfers", "Sensitive business meetings", "Full-day chauffeur service"],
      },
      es: {
        cat: "Limusina berlina primera clase",
        name: "BMW Serie 7",
        pax: "1 — 3 pasajeros · 3 maletas",
        feat: "Cuero Merino · Wi-Fi 5G · Climatización 4 zonas",
        description:
          "Nuestra limusina de referencia en París. Un habitáculo pensado como un salón rodante: insonorización avanzada, suspensión neumática.",
        equipment: ["Cuero Merino flor entera", "Climatización 4 zonas independientes", "Wi-Fi 5G ilimitado", "Asientos traseros masajeadores y reclinables", "Lunas acústicas tintadas", "Mesas de trabajo plegables", "Aguas Évian / Perrier de cortesía", "Prensa del día a petición"],
        usage: ["Traslados al aeropuerto premium", "Reuniones de negocios sensibles", "Chófer a disposición por día"],
      },
    },
  },
  {
    slug: "mercedes-classe-v",
    n: "N°02",
    i18n: {
      fr: {
        cat: "Van VIP",
        name: "Mercedes-Benz Classe V",
        pax: "1 — 7 passagers · bagages selon configuration",
        capacityNote: [
          "Jusqu'à 7 passagers : 6 à l'arrière et 1 à l'avant, sans compter le chauffeur.",
          "La capacité des bagages dépend de leur volume et de la configuration du véhicule.",
        ],
        feat: "Sièges capitaine · Wi-Fi 4G",
        description: "Le van VIP par excellence. Six sièges capitaine en cuir, configuration salon ou face-à-face, espace bagages généreux.",
        equipment: ["6 sièges capitaine cuir Nappa", "Configuration salon ou face-à-face", "Tablette centrale repliable", "Wi-Fi 4G illimité", "Toit ouvrant panoramique", "Vitres latérales privatives", "Espace bagages selon le volume et la configuration"],
        usage: ["Délégations et COMEX", "Familles avec enfants", "Transferts groupes aéroport", "Roadshows et conventions"],
      },
      en: {
        cat: "VIP Van",
        name: "Mercedes-Benz V-Class",
        pax: "1 — 7 passengers · luggage per configuration",
        capacityNote: [
          "Up to 7 passengers: 6 in the rear and 1 in the front passenger seat, excluding the chauffeur.",
          "Luggage capacity depends on luggage size and vehicle configuration.",
        ],
        feat: "Captain seats · 4G Wi-Fi",
        description: "The quintessential VIP van. Six leather captain seats, lounge or face-to-face configuration, generous luggage space.",
        equipment: ["6 Nappa leather captain seats", "Lounge or face-to-face configuration", "Folding central tablet", "Unlimited 4G Wi-Fi", "Panoramic sunroof", "Private side windows", "Luggage space depending on size and configuration"],
        usage: ["Delegations and COMEX", "Families with children", "Group airport transfers", "Roadshows and conventions"],
      },
      es: {
        cat: "Van VIP",
        name: "Mercedes-Benz Clase V",
        pax: "1 — 7 pasajeros · equipaje según configuración",
        capacityNote: [
          "Hasta 7 pasajeros: 6 en la parte trasera y 1 en el asiento delantero, sin contar al chófer.",
          "La capacidad de equipaje depende del volumen y de la configuración del vehículo.",
        ],
        feat: "Asientos capitán · Wi-Fi 4G",
        description: "El van VIP por excelencia. Seis asientos capitán en cuero, configuración salón o cara a cara, espacio generoso para equipaje.",
        equipment: ["6 asientos capitán cuero Nappa", "Configuración salón o cara a cara", "Mesa central plegable", "Wi-Fi 4G ilimitado", "Techo panorámico", "Lunas laterales privadas", "Espacio para equipaje según volumen y configuración"],
        usage: ["Delegaciones y COMEX", "Familias con niños", "Traslados de grupos al aeropuerto", "Roadshows y convenciones"],
      },
    },
  },
  {
    slug: "ds-9",
    n: "N°03",
    i18n: {
      fr: {
        cat: "Berline française",
        name: "DS 9",
        pax: "1 — 3 passagers · 3 bagages",
        feat: "Hybride rechargeable · Cuir Nappa · Audio Focal",
        description: "L'élégance française dans sa version la plus aboutie. Cuir Nappa point bracelet, système audio Focal Electra.",
        equipment: ["Cuir Nappa point bracelet", "Système audio Focal Electra 14 HP", "Motorisation hybride rechargeable", "Mode 100% électrique en ville", "Sièges chauffants et massants", "Affichage tête haute"],
        usage: ["Trajets urbains silencieux", "Soirées et événements", "Clientèle attachée au savoir-faire français"],
      },
      en: {
        cat: "French sedan",
        name: "DS 9",
        pax: "1 — 3 passengers · 3 bags",
        feat: "Plug-in hybrid · Nappa leather · Focal audio",
        description: "French elegance at its most accomplished. Watch-strap stitched Nappa leather, Focal Electra audio, plug-in hybrid powertrain for silent driving through Paris.",
        equipment: ["Watch-strap stitched Nappa leather", "Focal Electra 14-speaker audio", "Plug-in hybrid powertrain", "100% electric mode in the city", "Heated, massage seats", "Head-up display"],
        usage: ["Silent urban trips", "Evenings and events", "Clientele attached to French craftsmanship"],
      },
      es: {
        cat: "Berlina francesa",
        name: "DS 9",
        pax: "1 — 3 pasajeros · 3 maletas",
        feat: "Híbrido enchufable · Cuero Nappa · Audio Focal",
        description: "La elegancia francesa en su versión más lograda. Cuero Nappa con costura de pulsera, sistema de audio Focal Electra.",
        equipment: ["Cuero Nappa costura pulsera", "Sistema audio Focal Electra 14 altavoces", "Motorización híbrida enchufable", "Modo 100% eléctrico en ciudad", "Asientos calefactados y masajeadores", "Head-up display"],
        usage: ["Trayectos urbanos silenciosos", "Veladas y eventos", "Clientela apegada al saber hacer francés"],
      },
    },
  },
  {
    slug: "bmw-i7",
    n: "N°04",
    i18n: {
      fr: {
        cat: "Limousine 100% électrique",
        name: "BMW i7",
        pax: "1 — 3 passagers · 3 bagages",
        feat: "Zéro émission · Theatre Screen 31\" · B&W Diamond",
        description: "La limousine électrique de référence à Paris. Écran Theatre 31 pouces déployable depuis le plafond, système audio Bowers & Wilkins Diamond.",
        equipment: ["Theatre Screen 8K de 31 pouces", "Audio Bowers & Wilkins Diamond 36 HP", "Zéro émission directe", "Autonomie > 600 km", "Portes à fermeture assistée", "Verre teinté électrochrome", "Recharge ultra-rapide 195 kW"],
        usage: ["Clientèle ESG", "Longs trajets internationaux", "Événements à fort enjeu d'image"],
      },
      en: {
        cat: "100% electric limousine",
        name: "BMW i7",
        pax: "1 — 3 passengers · 3 bags",
        feat: "Zero emission · 31\" Theatre Screen · B&W Diamond",
        description: "The benchmark electric limousine in Paris. 31-inch Theatre Screen deploying from the ceiling, Bowers & Wilkins Diamond audio, over 600 km of range. Absolute silence.",
        equipment: ["31-inch 8K Theatre Screen", "36-speaker Bowers & Wilkins Diamond audio", "Zero direct emission", "Range > 600 km", "Soft-close doors", "Electrochromic tinted glass", "Ultra-fast 195 kW charging"],
        usage: ["ESG clientele", "Long international trips", "High-image events"],
      },
      es: {
        cat: "Limusina 100% eléctrica",
        name: "BMW i7",
        pax: "1 — 3 pasajeros · 3 maletas",
        feat: "Cero emisiones · Theatre Screen 31\" · B&W Diamond",
        description: "La limusina eléctrica de referencia en París. Pantalla Theatre de 31 pulgadas desplegable desde el techo, sistema de audio Bowers & Wilkins Diamond.",
        equipment: ["Pantalla Theatre 8K de 31 pulgadas", "Audio Bowers & Wilkins Diamond 36 altavoces", "Cero emisiones directas", "Autonomía > 600 km", "Puertas con cierre asistido", "Cristal tintado electrocrómico", "Carga ultrarrápida 195 kW"],
        usage: ["Clientela ESG", "Trayectos internacionales largos", "Eventos de alto impacto de imagen"],
      },
    },
  },
  {
    slug: "sprinter-vip",
    n: "N°05",
    i18n: {
      fr: {
        cat: "Sprinter VIP — groupes",
        name: "Mercedes-Benz Sprinter VIP",
        pax: "jusqu'à 16 — 18 passagers · bagages en soute",
        feat: "Climatisation · Vitres teintées · Espace bagages (selon configuration)",
        description: "Le véhicule de référence pour les groupes qui ne veulent pas renoncer au standing : jusqu'à 16–18 passagers selon configuration, confort supérieur.",
        equipment: ["16 à 18 places selon configuration", "Climatisation", "Vitres teintées (selon configuration)", "Sellerie soignée et éclairage d'ambiance", "Espace bagages dédié", "Prises de recharge (selon configuration)"],
        usage: ["Délégations et séminaires", "Transferts aéroport groupés", "Navettes événementielles"],
      },
      en: {
        cat: "VIP Sprinter — groups",
        name: "Mercedes-Benz VIP Sprinter",
        pax: "up to 16 — 18 passengers · hold luggage",
        feat: "Air conditioning · Tinted windows · Luggage space (depending on configuration)",
        description: "The reference vehicle for groups that will not compromise on standing: up to 16–18 passengers depending on configuration, enhanced comfort, air conditioning.",
        equipment: ["16 to 18 seats depending on configuration", "Air conditioning", "Tinted windows (depending on configuration)", "Refined upholstery and ambient lighting", "Dedicated luggage space", "Charging ports (depending on configuration)"],
        usage: ["Delegations and seminars", "Grouped airport transfers", "Event shuttles"],
      },
      es: {
        cat: "Sprinter VIP — grupos",
        name: "Mercedes-Benz Sprinter VIP",
        pax: "hasta 16 — 18 pasajeros · equipaje en maletero",
        feat: "Climatización · Cristales tintados · Espacio de equipaje (según configuración)",
        description: "El vehículo de referencia para grupos que no renuncian al nivel de servicio: hasta 16–18 pasajeros según configuración, mayor confort, climatización.",
        equipment: ["16 a 18 plazas según configuración", "Climatización", "Cristales tintados (según configuración)", "Tapicería cuidada e iluminación ambiental", "Espacio de equipaje dedicado", "Tomas de carga (según configuración)"],
        usage: ["Delegaciones y seminarios", "Traslados de grupo al aeropuerto", "Lanzaderas de eventos"],
      },
    },
  },
  {
    slug: "autocar",
    n: "N°06",
    i18n: {
      fr: {
        cat: "Autocar — grande capacité",
        name: "Autocar avec chauffeur",
        pax: "20 à 60+ passagers · soute bagages",
        feat: "Climatisation · Soute · Wi-Fi, USB, toilettes (selon configuration)",
        description: "Solution grande capacité pour congrès, événements d'entreprise, cérémonies, navettes hôtels / lieux d'événement et trajets longue distance.",
        equipment: ["20 à 60+ places selon le véhicule", "Climatisation", "Soute à bagages", "Wi-Fi (selon configuration)", "Prises USB (selon configuration)", "Toilettes et microphone (selon configuration)"],
        usage: ["Congrès et conventions", "Navettes hôtels / lieux d'événement", "Trajets longue distance France et Europe"],
      },
      en: {
        cat: "Coach — large capacity",
        name: "Coach bus with driver",
        pax: "20 to 60+ passengers · luggage hold",
        feat: "Air conditioning · Hold · Wi-Fi, USB, restrooms (depending on configuration)",
        description: "Large-capacity solution for conferences, corporate events, ceremonies, hotel-to-venue shuttles and long-distance journeys.",
        equipment: ["20 to 60+ seats depending on the vehicle", "Air conditioning", "Luggage hold", "Wi-Fi (depending on configuration)", "USB ports (depending on configuration)", "Restrooms and microphone (depending on configuration)"],
        usage: ["Conferences and conventions", "Hotel-to-venue shuttles", "Long-distance journeys in France and Europe"],
      },
      es: {
        cat: "Autocar — gran capacidad",
        name: "Autocar con chófer",
        pax: "20 a 60+ pasajeros · maletero",
        feat: "Climatización · Maletero · Wi-Fi, USB, aseos (según configuración)",
        description: "Solución de gran capacidad para congresos, eventos corporativos, ceremonias, lanzaderas hotel / lugar de evento y trayectos de larga distancia.",
        equipment: ["20 a 60+ plazas según el vehículo", "Climatización", "Maletero", "Wi-Fi (según configuración)", "Puertos USB (según configuración)", "Aseos y micrófono (según configuración)"],
        usage: ["Congresos y convenciones", "Lanzaderas hotel / lugar del evento", "Trayectos de larga distancia por Francia y Europa"],
      },
    },
  },
];


export const CARS: Car[] = RAW_CARS.map((c) => ({ ...c, image: CAR_IMAGES[c.slug] }));

